import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Card, Empty, Space, Spin, Tag, Typography } from 'antd';
import { ArrowRightOutlined } from '@ant-design/icons';
import { registryRecordApi, type RegistryProgram, type RegistryRecord } from '../../api/registryRecordApi';
import { PHASE_STATUS_META, type PhaseDef, type PhaseStatus } from './RegistryPhaseWorkspace';
import { registryLabel } from './registryDocGroups';

const { Text } = Typography;

/** Configurația cartelei compacte din fișa pacientului spre spațiul de lucru pe faze. */
export interface RegistryEntryConfig {
  program: RegistryProgram;
  /** ex. „Fișă de registru hepato” */
  title: string;
  /** ex. „Deschide spațiul de lucru hepato” */
  buttonLabel: string;
  buttonTestId: string;
  /** ex. „hepato” → /patients/:id/hepato/faza-1 */
  routeSegment: 'hepato' | 'dermato';
  phases: readonly PhaseDef[];
  computeStatuses: (record: RegistryRecord) => Record<number, PhaseStatus>;
  flowDescription: string;
}

/**
 * Cartela compactă din fișa pacientului pentru registrul importat:
 * sumar pe faze + link spre spațiul de lucru (/patients/:id/<segment>/faza-1).
 */
export default function RegistryEntryCard({ config, patientId }: { config: RegistryEntryConfig; patientId: string }) {
  const navigate = useNavigate();
  const [record, setRecord] = useState<RegistryRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    registryRecordApi
      .get(config.program, patientId)
      .then((r) => !cancelled && setRecord(r))
      .catch(() => !cancelled && setError('Fișa de registru nu a putut fi încărcată'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [patientId, config.program]);

  if (loading) {
    return (
      <Card title={config.title} style={{ marginBottom: 16 }}>
        <Spin />
      </Card>
    );
  }
  if (error) {
    return (
      <Card title={config.title} style={{ marginBottom: 16 }}>
        <Alert type="error" message={error} />
      </Card>
    );
  }
  if (!record) {
    return (
      <Card title={config.title} style={{ marginBottom: 16 }}>
        <Empty description="Nicio fișă de registru: pacientul nu a fost importat și aprobat din documente (Import în lot)." />
      </Card>
    );
  }

  const statuses = config.computeStatuses(record);

  return (
    <Card
      title={config.title}
      style={{ marginBottom: 16 }}
      extra={
        <Space wrap>
          <Tag color={record.validationStatus === 'VALIDATED' ? 'green' : 'orange'}>
            {registryLabel(record.validationStatus)}
          </Tag>
          <Button
            type="primary"
            size="small"
            icon={<ArrowRightOutlined />}
            data-testid={config.buttonTestId}
            onClick={() => navigate(`/patients/${patientId}/${config.routeSegment}/faza-1`)}
          >
            {config.buttonLabel}
          </Button>
        </Space>
      }
    >
      <Text type="secondary">{config.flowDescription} </Text>
      <Space wrap size={[4, 4]} style={{ marginTop: 8 }}>
        {config.phases.map((p) => {
          const meta = PHASE_STATUS_META[statuses[p.phase] ?? 'MISSING'];
          return (
            <Tag key={p.phase} color={meta.color} data-testid={`entry-phase-status-${p.phase}`}>
              Faza {p.phase}: {meta.label}
            </Tag>
          );
        })}
      </Space>
    </Card>
  );
}
